import { useState, useEffect } from 'react'
import { getAdminProjects, addProject, updateProject, deleteProject, restoreProject } from '../services/dbService'
import { formatTimestamp } from '../utils/helpers'

export default function AdminProjects() {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  
  // Form State
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({
    title: '', category: 'Residential', image: '', location: '', description: '', featured: false, status: 'completed'
  })
  const [formLoading, setFormLoading] = useState(false)

  const fetchProjects = async () => {
    setLoading(true)
    setError('')
    try {
      const data = await getAdminProjects()
      setProjects(data)
    } catch (err) {
      setError('Failed to fetch projects. ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProjects()
  }, [])

  const resetForm = () => {
    setFormData({ title: '', category: 'Residential', image: '', location: '', description: '', featured: false, status: 'completed' })
    setEditingId(null)
    setShowForm(false)
  }

  const handleEdit = (project) => {
    setFormData({
      title: project.title || '',
      category: project.category || 'Residential',
      image: project.image || '',
      location: project.location || '',
      description: project.description || '',
      featured: project.featured || false,
      status: project.status || 'completed'
    })
    setEditingId(project.id)
    setShowForm(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.title || !formData.image || !formData.description) {
      alert('Title, Image, and Description are required.')
      return
    }
    
    setFormLoading(true)
    setError('')
    try {
      if (editingId) {
        await updateProject(editingId, formData)
      } else {
        await addProject(formData)
      }
      resetForm()
      await fetchProjects() // Refresh list
    } catch (err) {
      setError('Failed to save project. ' + err.message)
    } finally {
      setFormLoading(false)
    }
  }

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to soft-delete "${title}"?`)) {
      try {
        await deleteProject(id, false)
        await fetchProjects()
      } catch (err) {
        setError('Failed to delete project. ' + err.message)
      }
    }
  }

  const handleRestore = async (id) => {
    try {
      await restoreProject(id)
      await fetchProjects()
    } catch (err) {
      setError('Failed to restore project. ' + err.message)
    }
  }

  if (loading) return <div>Loading projects...</div>

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.2rem', color: '#7a8fa6', margin: 0 }}>Manage Projects ({projects.length})</h2>
        <button onClick={() => { resetForm(); setShowForm(true) }} className="btn-gold" style={{ padding: '8px 16px', fontSize: '0.9rem' }}>
          + Add Project
        </button>
      </div>

      {error && <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: 12, borderRadius: 8, marginBottom: 20 }}>{error} <button onClick={fetchProjects} style={{ background: 'transparent', border: '1px solid currentColor', color: 'inherit', padding: '2px 8px', borderRadius: 4, marginLeft: 10, cursor: 'pointer' }}>Retry</button></div>}

      {showForm && (
        <div style={{ background: 'rgba(255,255,255,0.05)', padding: 24, borderRadius: 12, marginBottom: 30 }}>
          <h3 style={{ marginTop: 0, marginBottom: 20 }}>{editingId ? 'Edit Project' : 'New Project'}</h3>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <input type="text" placeholder="Title *" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required style={inputStyle} />
              <select value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} style={inputStyle}>
                <option value="Residential">Residential</option>
                <option value="Commercial">Commercial</option>
                <option value="Industrial">Industrial</option>
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
              <input type="url" placeholder="Image URL *" value={formData.image} onChange={e => setFormData({...formData, image: e.target.value})} required style={inputStyle} />
              <input type="text" placeholder="Location" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} style={inputStyle} />
            </div>
            
            {formData.image && (
              <div style={{ marginTop: 10, marginBottom: 10 }}>
                <span style={{ fontSize: '0.8rem', color: '#7a8fa6', display: 'block', marginBottom: 5 }}>Image Preview:</span>
                <img src={formData.image} alt="Preview" onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Invalid+Image+URL' }} style={{ height: 100, objectFit: 'cover', borderRadius: 8 }} />
              </div>
            )}

            <textarea placeholder="Description (Max 300 chars) *" maxLength={300} rows={4} value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} required style={inputStyle} />
            
            <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={formData.featured} onChange={e => setFormData({...formData, featured: e.target.checked})} />
                Featured Project
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                Status: 
                <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} style={{...inputStyle, padding: '4px 8px', width: 'auto'}}>
                  <option value="completed">Completed</option>
                  <option value="ongoing">Ongoing</option>
                </select>
              </label>
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
              <button type="submit" disabled={formLoading} className="btn-gold" style={{ padding: '10px 24px' }}>
                {formLoading ? 'Saving...' : 'Save Project'}
              </button>
              <button type="button" onClick={resetForm} disabled={formLoading} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '10px 24px', borderRadius: 8, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {projects.length === 0 ? (
        <div style={{ background: 'rgba(255,255,255,0.03)', padding: 40, borderRadius: 12, textAlign: 'center', color: '#7a8fa6' }}>
          No projects found.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
          {projects.map(p => (
            <div key={p.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, overflow: 'hidden', border: p.isDeleted ? '1px dashed #ef4444' : '1px solid transparent', opacity: p.isDeleted ? 0.7 : 1 }}>
              <img src={p.image} alt={p.title} onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Image+Error' }} style={{ width: '100%', height: 160, objectFit: 'cover' }} />
              <div style={{ padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <h4 style={{ margin: 0, fontSize: '1.1rem' }}>{p.title} {p.featured && <i className="fas fa-star" style={{ color: '#f5c518', fontSize: '0.8rem' }} />}</h4>
                  <span style={{ fontSize: '0.75rem', background: p.status === 'ongoing' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(34, 197, 94, 0.2)', color: p.status === 'ongoing' ? '#60a5fa' : '#4ade80', padding: '2px 8px', borderRadius: 10 }}>{p.status}</span>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#7a8fa6', marginBottom: 12 }}>{p.category} | {p.location || 'No location'}</div>
                {p.isDeleted && <div style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: 'bold', marginBottom: 10 }}>[DELETED - TRASH]</div>}
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button onClick={() => handleEdit(p)} style={actionBtnStyle}>Edit</button>
                  {p.isDeleted ? (
                    <button onClick={() => handleRestore(p.id)} style={{...actionBtnStyle, color: '#4ade80', borderColor: 'rgba(34,197,94,0.3)'}}>Restore</button>
                  ) : (
                    <button onClick={() => handleDelete(p.id, p.title)} style={{...actionBtnStyle, color: '#f87171', borderColor: 'rgba(239,68,68,0.3)'}}>Delete</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const inputStyle = {
  width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.2)', color: 'white', fontFamily: "'Inter', sans-serif"
}

const actionBtnStyle = {
  flex: 1, background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '6px 0', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', textTransform: 'uppercase', fontWeight: 600
}
